import moment from 'moment'

export const parseUserId = ud2 => {
    const m = String(ud2 || '').match(/(\d{3,})/)
    return m ? m[1] : ''
}

export const parseUserName = ud2 =>
    String(ud2 || '')
        .replace(/\d+/g, '')
        .replace(/\s*-\s*$/, '')
        .trim()

export const formatPhone = p => {
    const s = String(p || '').replace(/[-\s]/g, '')
    return s ? (/^0/.test(s) ? s : '0' + s) : ''
}

export const formatAmount = a => {
    if (a == null || a === '') return ''
    const n = Number(a) / 100
    return (Number.isInteger(n) ? String(n) : n.toFixed(2)) + ' ₪'
}

export const cardLast4 = tok => {
    const s = String(tok || '')
    return s.length >= 4 ? s.slice(-4) : ''
}

const normalizeFilterText = value =>
    String(value == null ? '' : value)
        .trim()
        .toLowerCase()

export const CARD_TRANSACTION_FILTERS = [
    { key: 'fUserId', label: 'מזהה לקוח', dir: 'ltr', accessor: t => parseUserId(t.user_defined_2) },
    { key: 'fEmail', label: 'אימייל', dir: 'ltr', accessor: t => t.user_defined_3 },
    { key: 'fPhone', label: 'טלפון', dir: 'ltr', accessor: t => t.user_defined_5, normalize: formatPhone },
    { key: 'fCardLast4', label: '4 ספרות אחרונות', dir: 'ltr', accessor: t => cardLast4(t.credit_card_token) },
    { key: 'fToken', label: 'טוקן', dir: 'ltr', accessor: t => t.credit_card_token },
    { key: 'fCardOwnerId', label: 'ת"ז בעל הכרטיס', dir: 'ltr', accessor: t => t.credit_card_owner_id }
]

export const BANK_TRANSACTION_FILTERS = [
    { key: 'fUserId', label: 'מזהה לקוח', dir: 'ltr', accessor: t => parseUserId(t.user_defined_2) },
    { key: 'fEmail', label: 'אימייל', dir: 'ltr', accessor: t => t.user_defined_3 },
    { key: 'fBank', label: 'בנק', dir: 'ltr', accessor: t => t.bank },
    { key: 'fBankBranch', label: 'סניף', dir: 'ltr', accessor: t => t.bank_branch },
    { key: 'fBankAccount', label: 'חשבון', dir: 'ltr', accessor: t => t.bank_account }
]

export const ALL_TRANSACTION_FILTERS = [
    ...CARD_TRANSACTION_FILTERS,
    ...BANK_TRANSACTION_FILTERS.filter(filter => ['fBank', 'fBankBranch', 'fBankAccount'].includes(filter.key))
]

export const ALL_TRANSACTION_FILTER_KEYS = [
    'fUserId',
    'fEmail',
    'fPhone',
    'fCardLast4',
    'fToken',
    'fCardOwnerId',
    'fBank',
    'fBankBranch',
    'fBankAccount'
]

export const CLIENT_SEARCH_FILTER_KEY_BY_PARAM = {
    client_id: 'fUserId',
    email: 'fEmail',
    phone: 'fPhone',
    token: 'fToken',
    last4: 'fCardLast4',
    owner_id: 'fCardOwnerId',
    bank: 'fBank',
    bank_branch: 'fBankBranch',
    bank_account: 'fBankAccount'
}

export const emptyTransactionFilters = () =>
    ALL_TRANSACTION_FILTER_KEYS.reduce((values, key) => {
        values[key] = ''
        return values
    }, {})

export const TRANZILA_TERMINALS = ['mediwho1', 'mediwho1tok', 'mediwhobk']
export const DEFAULT_TERMINALS = [...TRANZILA_TERMINALS]

export const transactionFilterCandidatesForTerminals = terminals => {
    const selected = Array.isArray(terminals) ? terminals : []
    const hasBank = selected.includes('mediwhobk')
    const hasCard = selected.some(terminal => terminal === 'mediwho1' || terminal === 'mediwho1tok')

    if (!selected.length || (hasBank && hasCard)) return ALL_TRANSACTION_FILTERS
    return hasBank ? BANK_TRANSACTION_FILTERS : CARD_TRANSACTION_FILTERS
}

const hasFilterValue = value => value != null && String(value).trim() !== ''

// Candidate membership and enablement are properties of the raw response, not `shown` or requested terminals.
export const transactionFiltersForRows = list => {
    if (!list || !list.length) return []
    const presentTerminals = list.reduce((terminals, transaction) => {
        const terminal = String(transaction.child_terminal || '').trim()
        if (terminal && !terminals.includes(terminal)) terminals.push(terminal)
        return terminals
    }, [])
    const candidates = transactionFilterCandidatesForTerminals(presentTerminals)
    return candidates.map(filter => ({
        ...filter,
        disabled: !list.some(transaction => hasFilterValue(filter.accessor(transaction)))
    }))
}

export const inactiveTransactionFilters = activeDefinitions => {
    const activeKeys = activeDefinitions.filter(filter => !filter.disabled).map(filter => filter.key)
    return ALL_TRANSACTION_FILTER_KEYS.reduce((values, key) => {
        if (!activeKeys.includes(key)) values[key] = ''
        return values
    }, {})
}

export const matchesTransactionFilters = (transaction, definitions, values) =>
    definitions.every(filter => {
        if (filter.disabled) return true
        const normalize = filter.normalize || normalizeFilterText
        const query = normalize(values[filter.key])
        return !query || normalize(filter.accessor(transaction)).includes(query)
    })

export const RESPONSE_TEXT = {
    '001': 'כרטיס חסום',
    '002': 'כרטיס גנוב',
    '003': 'התקשר לחברת האשראי לאישור',
    '004': 'סירוב מחברת אשראי',
    '015': 'כרטיס פג תוקף',
    '120': 'חסרה כניסה בוקטור 90',
    '300': 'אין הרשאת סולק לסוג עסקה',
    '341': 'אין הרשאת מנפיק לביצוע העסקה בסוג הכרטיס זה, יש לנסות שנית כרטיס אחר',
    '447': 'מספר כרטיס שגוי',
    '478': 'עסקת תורן בהוראת קבע מחייבת מספר אישור של עסקת איתחול',
    '888': 'נחסמה עסקה כפולה. אם צריך יש לנסות שוב מאוחר יותר',
    '125': 'שגיאת אמצעי תשלום — נדרש עדכון כרטיס',
    '344': 'אין הרשאת מנפיק לביצוע העסקה בסוג הכרטיס זה, יש לנסות שנית כרטיס אחר',
    '099': 'תגובה לא תקינה משרת שב"א. יש לשלוח את העסקה שנית',
    '418': 'חסרים פרמטרים חיוניים',
    '009': 'נתק בתקשורת',
    '445': 'בכרטיס חיוב מיידי ניתן לבצע אשראי חיוב מיידי בלבד',
    '033': 'כרטיס לא תקין',
    '073': 'יש לבצע שידור ידני במסוף לצורך עדכון ווקטורים ופרמטרים ולוודא שתקשורת עברה בהצלחה. לאחר מכן יש לנסות שנית',
    '702': 'קוד סניף לא תקין',
    '703': 'מספר חשבון לא תקין',
    '704': 'צירוף בנק/סניף/חשבון לא תקין'
}

// unknown codes have no text — show a word, not a bare number
export const UNKNOWN_RESPONSE_TEXT = 'לא ידוע'

export const responseText = raw => {
    const s = String(raw == null ? '' : raw).trim()
    if (!s) return ''
    const digits = s.replace(/^-/, '')
    const padded = digits.length < 3 ? ('00' + digits).slice(-3) : digits
    return (
        RESPONSE_TEXT[s] || RESPONSE_TEXT[digits] || RESPONSE_TEXT[padded] || s // fallback: show the raw code
    )
}

// --- code decodings. Derived from real data (checked against the Tranzila UI on עסקה 8548),
// not from official docs: an unknown code is always shown raw, never empty.
export const lookup = (map, v) => (v == null || v === '' ? '' : map[String(v)] || String(v))

// סוג עסקה — by the first letter of tranmode (values look like 'A688137')
export const TRANMODE_TEXT = {
    A: 'חיוב',
    C: 'זיכוי',
    D: 'ביטול',
    J: 'אימות הו"ק',
    T: 'הוראת קבע',
    N: 'טכני'
}
export const tranmodeLetter = t =>
    String((t && t.tranmode) || '')
        .trim()
        .charAt(0)
        .toUpperCase()
export const tranmodeText = t => TRANMODE_TEXT[tranmodeLetter(t)] || String((t && t.tranmode) || '')
export const isBankTxn = t => tranmodeLetter(t) === 'T'

export const PAYMENT_PLAN_TEXT = { '1': 'רגיל', '3': 'חיוב מיידי' }
export const CARD_ISSUER_TEXT = { '1': 'ישראכרט', '2': 'כאל', '3': 'אמריקן אקספרס', '4': 'דיינרס', '6': 'מקס' }
export const CLEARING_TEXT = { '1': 'ישראכרט', '2': 'כאל', '6': 'מקס' }
export const CARD_BRAND_TEXT = { '1': 'מאסטרקארד', '2': 'ויזה' }
export const FOREIGN_TEXT = { '0': 'ישראלי', '1': 'חו"ל' }

// as Tranzila shows it: DD/MM/YY HH:mm
export const txnDateTime = t =>
    moment(`${t.transaction_date} ${t.transaction_time}`, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YY HH:mm')

// the modal shows the amount with two decimals (the table keeps the format without .00)
export const amountFull = a => (a == null || a === '' ? '—' : '₪ ' + (Number(a) / 100).toFixed(2))

// --- עסקה line items (items[]) for the modal. Loaded lazily, in a separate request per transaction.

// DISPLAY ONLY. Order is preserved, duplicates are not collapsed, administrative codes (H-0010/H-0015)
// are not stripped: the modal shows the real Tranzila rows, not the SKU resolver's output.
// An invalid response (missing / not an array) is a transaction without items, not an error.
export const normalizeTransactionItems = items => {
    if (!Array.isArray(items)) return []
    return items.filter(item => !!item && typeof item === 'object' && !Array.isArray(item))
}

// items[].price comes in AGOROT — proven by cross-checking the Tranzila page for mediwhobk/28273:
// 21355 -> 213.55 ₪, 254 -> 2.54 ₪. Divide by 100, same as amountFull/formatAmount for transaction.amount.
// The API sanitizer lets through a string or a finite number, so we accept both.
// Strings are validated strictly and in full: parseFloat('12abc') would yield 0.12 ₪ from garbage,
// and Number('') and Number('   ') give 0 — reject the empty string before converting.
// Zero is a valid price, so we check the type, not truthiness.
export const formatTransactionItemPrice = value => {
    let amount
    if (typeof value === 'number') {
        amount = value
    } else if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed === '') return '—'
        amount = Number(trimmed)
    } else {
        return '—'
    }
    return isFinite(amount) ? (amount / 100).toFixed(2) + ' ₪' : '—'
}

// user_defined_9 (VAT) comes as a fraction: 0.18 -> 18%. Same strict parsing as the price.
// Only 0..1 is converted; anything else is shown raw so an unexpected format stays visible, not mangled.
// toFixed(2) + Number() kills float noise (0.18 * 100 = 18.000000000000004).
export const formatVatRate = value => {
    const trimmed = typeof value === 'string' ? value.trim() : value
    const rate = typeof trimmed === 'number' || (typeof trimmed === 'string' && trimmed !== '') ? Number(trimmed) : NaN
    if (!isFinite(rate) || rate < 0 || rate > 1) return itemDisplayValue(value)
    return Number((rate * 100).toFixed(2)) + '%'
}

// `val` from the modal doesn't protect against an object/array: they would render as [object Object].
// Numbers (including 0) are valid values, so we check the type, not truthiness.
export const itemDisplayValue = value => {
    if (value == null) return '—'
    if (typeof value === 'number') return isFinite(value) ? String(value) : '—'
    if (typeof value !== 'string') return '—'
    return value.trim() === '' ? '—' : value
}

export const initialTransactionDateRange = () => ({ Fdate: moment().subtract(1, 'day'), Tdate: moment() })
export const DEFAULT_CATEGORIES = ['recurring_failed']

// UI categories declaratively define the allowed groups and, if needed, an extra predicate.
// billingCountKeys lets SearchBar use the more precise aggregates of the new billing API.
export const CATEGORIES = [
    {
        key: 'recurring_failed',
        label: 'חיוב חודשי שנכשל',
        groups: ['card_failed', 'bank_failed'],
        match: transaction => transaction.billing_kind === 'recurring',
        billingCountKeys: ['recurring_failed'],
        colorGroup: 'card_failed'
    },
    {
        key: 'oneoff_failed',
        label: 'חיוב חד-פעמי שנכשל',
        groups: ['card_failed'],
        match: transaction => transaction.billing_kind === 'oneoff',
        billingCountKeys: ['oneoff_failed'],
        colorGroup: 'card_failed'
    },
    { key: 'zikuy_failed', label: 'זיכוי שנכשל', groups: ['zikuy_failed'], colorGroup: 'zikuy_failed' },
    { key: 'sto_failed', label: 'אימות הו"ק שנכשל', groups: ['sto_verify_failed'], colorGroup: 'sto_verify_failed' },
    {
        key: 'recurring_success',
        label: 'חיוב חודשי שהצליח',
        groups: ['card_success', 'bank_success'],
        match: transaction => transaction.billing_kind === 'recurring',
        billingCountKeys: ['recurring_success'],
        colorGroup: 'card_success'
    },
    {
        key: 'oneoff_success',
        label: 'חיוב חד-פעמי שהצליח',
        groups: ['card_success'],
        match: transaction => transaction.billing_kind === 'oneoff',
        billingCountKeys: ['oneoff_success'],
        colorGroup: 'card_success'
    },
    { key: 'zikuy_ok', label: 'זיכוי שהצליח', groups: ['zikuy_success'], colorGroup: 'zikuy_success' },
    { key: 'sto_ok', label: 'אימות הו"ק שהצליח', groups: ['sto_verify_ok'], colorGroup: 'sto_verify_ok' },
    {
        key: 'pending',
        label: 'ממתין לתשובה',
        groups: ['bank_pending'],
        billingCountKeys: ['recurring_pending'],
        colorGroup: 'bank_pending'
    },
    { key: 'cancel', label: 'ביטול', groups: ['cancel_success', 'cancel_failed'], colorGroup: 'cancel_success' }
]
// 'junk' has no category of its own: technical noise is visible when no category is selected (= הכל).

export const matchesCategory = (transaction, category) =>
    (!category.groups || category.groups.includes(transaction.group)) &&
    (!category.match || category.match(transaction))

// empty category selection = הכל, so the list is returned as is
export const rowsForCategories = (list, categories) => {
    const selected = CATEGORIES.filter(category => categories.includes(category.key))
    return selected.length
        ? list.filter(transaction => selected.some(category => matchesCategory(transaction, category)))
        : list
}

export const isFailureGroup = g =>
    ['card_failed', 'bank_failed', 'zikuy_failed', 'sto_verify_failed', 'cancel_failed'].includes(g)

export const transactionErrorCode = transaction => {
    const raw = transaction && transaction.processor_response_code
    return String(raw == null ? '' : raw).trim()
}

// Choices are built from the rows the category filter already kept, so the operator never sees an
// error that cannot occur in the current category. Callers must drop selected codes that disappear
// from the new option set — otherwise a stale code silently filters the table down to nothing.
// Literal codes stay distinct; responseText is only their operator-facing label.
export const transactionErrorOptions = list => {
    const counts = (list || []).reduce((byCode, transaction) => {
        const code = transactionErrorCode(transaction)
        if (code && isFailureGroup(transaction.group)) byCode[code] = Number(byCode[code] || 0) + 1
        return byCode
    }, {})

    return Object.keys(counts)
        .sort((a, b) => Number(a) - Number(b) || a.localeCompare(b))
        .map(code => {
            const text = responseText(code)
            return {
                value: code,
                label: `${code} — ${text && text !== code ? text : UNKNOWN_RESPONSE_TEXT}`,
                count: counts[code]
            }
        })
}

export const matchesTransactionErrors = (transaction, errorCodes) =>
    !Array.isArray(errorCodes) ||
    !errorCodes.length ||
    errorCodes.includes(transactionErrorCode(transaction))

export const statusText = r =>
    r.group === 'junk'
        ? 'ללא סטטוס'
        : r.group === 'bank_pending'
        ? 'ממתין לתשובה'
        : isFailureGroup(r.group)
        ? responseText(r.processor_response_code)
        : r.group === 'cancel_success'
        ? 'ביטול בוצע'
        : 'הצליח'

// antd Tag presets; the modal colors text, so the same keys need a hex
// 'grey' is not an antd Tag preset: it goes into an inline background-color, which is a valid CSS color
export const statusColor = g =>
    g === 'junk'
        ? 'grey'
        : g === 'bank_pending'
        ? 'orange'
        : isFailureGroup(g)
        ? 'red'
        : g === 'cancel_success'
        ? 'blue'
        : 'green'

export const STATUS_HEX = { orange: '#fa8c16', red: '#f5222d', blue: '#1890ff', green: '#52c41a', grey: '#8c8c8c' }

// The category color goes through the same statusColor -> STATUS_HEX as the table tags.
// colorGroup holds only a representative backend group; there is no separate palette for Select.
export const categoryColor = category => {
    if (!category || !category.colorGroup) return undefined
    return STATUS_HEX[statusColor(category.colorGroup)]
}

// Charge rows use billing_kind; all other rows retain the existing group-based label.
// String input remains a compatibility fallback for older pure-logic callers.
export const operationType = row => {
    const transaction = typeof row === 'string' ? { group: row } : row || {}
    const s = String(transaction.group || '')
    const isCharge = s.indexOf('card_') === 0 || s.indexOf('bank_') === 0
    if (isCharge && transaction.billing_kind === 'recurring') return 'חיוב חודשי'
    if (isCharge && transaction.billing_kind === 'oneoff') return 'חיוב חד-פעמי'
    if (s.indexOf('card_') === 0) return 'חיוב אשראי'
    if (s.indexOf('bank_') === 0) return 'הוראת קבע'
    if (s.indexOf('zikuy_') === 0) return 'זיכוי'
    if (s.indexOf('sto_') === 0) return 'אימות הו"ק'
    if (s.indexOf('cancel_') === 0) return 'ביטול'
    if (s === 'junk') return 'טכני'
    return ''
}

// --- הוראות קבע כפולות (nightly scan) ---

export const dupDate = d => (d ? moment(d).format('DD/MM/YY HH:mm') : '—')

// the he locale is stripped from the bundle (config/plugins.js, localesToKeep) — fromNow() would give English,
// so by default we show the date. The check is at runtime: if 'he' is added, it switches automatically.
export const scanAge = d => (!d ? '—' : /^he/.test(moment.locale()) ? moment(d).fromNow() : dupDate(d))

// the widget color is determined by the LAST SUCCESSFUL scan.
// branch order matters: is_stale is checked before green (green on stale data is a
// false "all clear"), but AFTER red: duplicates that were found are still there even on stale data.
export const dupView = d => {
    if (d.status === 'running') return { color: 'blue', text: 'סריקה מתבצעת', spin: true }
    if (d.status === 'none' || !d.scanned_at) return { color: 'grey', text: 'לא בוצעה סריקה' }
    if (d.unresolved_users_count > 0)
        return { color: 'red', text: `נמצאו ${d.unresolved_users_count} לקוחות עם הוראות קבע כפולות` }
    if (d.is_stale) return { color: 'grey', text: 'סריקה לא עדכנית' }
    return { color: 'green', text: 'אין הוראות קבע כפולות' }
}

// unresolved first, within them duplicate before migration_pair
export const dupUserOrder = (a, b) =>
    Number(!!a.resolved) - Number(!!b.resolved) || (a.type === 'duplicate' ? 0 : 1) - (b.type === 'duplicate' ? 0 : 1)

// single place for warning priority: "process died mid-scan" outranks "job didn't run",
// which outranks "last attempt failed". stuck_run is checked BEFORE running: a stuck scan stays
// in running status, and that is exactly what distinguishes it from one honestly running right now (no warning for that).
export const dupScanIssue = s => {
    if (!s) return null
    if (s.stuck_run) return 'stuck'
    if (s.status === 'running') return null
    if (s.missed_run) return 'missed'
    return s.last_attempt && s.last_attempt.status === 'failed' ? 'failed' : null
}

// --- "הצג גם שטופלו": composition of the modal table.

// users[] is the CURRENT scan, history_users[] are previously resolved cases no longer in the current scan
// (the duplicate was actually fixed, the client dropped out of the scan). Kept separate: the widget counters
// describe only the current state, and history must not inflate them.
// concat returns a new array, so sort doesn't mutate the API response.
export const dupRowsForDisplay = (data, showResolved) => {
    const current = ((data && data.users) || []).filter(u => showResolved || !u.resolved)
    const history = showResolved ? (data && data.history_users) || [] : []
    return current.concat(history).sort(dupUserOrder)
}

// user_id no longer works as a key: one client can legitimately have both a current unresolved
// row and a historical resolved one (the STO signature changed). The history key is the id of the
// resolution itself, which is unique; nothing random.
export const dupRowKey = row =>
    row && row.historical ? `history:${row.history_id}` : `current:${row && row.user_id}`

// Historical rows are view-only: the duplicate they describe may no longer exist,
// so resolve/unresolve is not allowed on them.
export const dupRowActionable = row => !!row && !row.historical

// Resolution metadata for the status cell: null means the row is unresolved, nothing to show.
// Works the same for current and historical rows: the API returns resolved_by_name for both.
// Fallback is the same dash as everywhere else in the modal; resolved_by (internal id) is never exposed.
export const dupResolutionMeta = row => {
    if (!row || !row.resolved) return null
    const name = row.resolved_by_name
    return {
        by: typeof name === 'string' && name.trim() !== '' ? name : '—',
        at: dupDate(row.resolved_at)
    }
}

// --- coordination of async status requests.

// A second history request is not needed while the first is still in flight: OFF→ON during H1 must stay silent.
// historyLoaded and historyLoading are DIFFERENT facts: "cache exists" and "request in flight".
export const shouldLoadStoHistory = state => !!state && !state.historyLoaded && !state.historyLoading

// A refresh after resolve/unresolve must keep history mode if history is visible, cached
// OR STILL IN FLIGHT. Without historyLoading the new request would go out without history, and the old H1 response would look
// "fuller" than the new one — exactly the race that caused the record to be lost.
export const stoRefreshNeedsHistory = state =>
    !!state && (!!state.showResolved || !!state.historyLoaded || !!state.historyLoading)

// The last STARTED request wins, not the last one to respond: network response order
// is not guaranteed. Same technique as dataRequestId on the Tranzila page.
export const stoRequestIsCurrent = (requestId, currentRequestId) => requestId === currentRequestId

// Closing the modal resets only UI state. historyLoaded and already loaded history_users
// are intentionally NOT included: the cache must survive closing, otherwise re-enabling the checkbox
// would again make the server parse the payload of old scans.
export const dupCloseState = () => ({ open: false, showResolved: false })
// page lives in the modal itself, so it has its own half of the reset.
export const dupModalCloseState = () => ({ page: 1 })

// STO charge_amount is already in shekels ('267.00'), unlike transaction amount (agorot).
// checked against StoTab.tsx and SearchStoData/StoDetailsModal.formatMoney — both print without dividing by 100.
export const stoAmount = a => {
    const n = Number(a)
    if (a == null || a === '' || isNaN(n)) return String(a == null ? '' : a)
    return Number.isInteger(n) ? String(n) : n.toFixed(2)
}
